import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { engine } from "@/audio/engine";
import { encodeWav, synthDemoVocal, trimStart, sliceBuffer } from "@/audio/dsp";
import { newTrack, MAX_UPLOAD_MB } from "@/lib/music";
import { useAuth } from "@/lib/auth";
import { ShareDialog } from "@/components/studio/ShareDialog";
import { DropOverlay } from "@/components/studio/DropOverlay";
import { StudioHeader } from "@/components/studio/StudioHeader";
import { Transport } from "@/components/studio/Transport";
import { HarmonyForge } from "@/components/studio/HarmonyForge";
import { Lanes } from "@/components/studio/Lanes";
import { ChannelStrip } from "@/components/studio/ChannelStrip";
import { RecordDialog } from "@/components/studio/RecordDialog";

const strip = ({ id, created_at, updated_at, ...rest }) => rest;

export default function Studio() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user, isPro, openUpgrade } = useAuth();
  const [project, setProject] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [, setBufVersion] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [busy, setBusy] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [recOpen, setRecOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const booted = useRef(false);
  const uploadRef = useRef(null);
  const songRef = useRef(null);
  const projectRef = useRef(null);
  projectRef.current = project;

  const loadTrack = useCallback(async (t, meta) => {
    try {
      await engine.loadTrack(t, meta || projectRef.current);
    } catch (e) {
      toast.error(`Could not load ${t.name}: ${errMsg(e)}`);
    }
    setBufVersion((v) => v + 1);
  }, []);

  const change = (fn) => {
    setProject((p) => fn(p));
    setDirty(true);
  };
  const updateTrack = (tid, patch) => change((p) => ({ ...p, tracks: p.tracks.map((t) => (t.id === tid ? { ...t, ...patch } : t)) }));
  const updateMaster = (patch) => change((p) => ({ ...p, master: { ...p.master, ...patch } }));

  const haltPlayback = () => {
    engine.stop();
    setPlaying(false);
    setTime(0);
  };

  const addTracks = async (tracks) => {
    haltPlayback();
    change((p) => ({ ...p, tracks: [...p.tracks, ...tracks] }));
    setSelectedId(tracks[0].id);
    for (const t of tracks) await loadTrack(t);
  };

  const addFile = async (blob, filename, name, extra = {}, decoded = null) => {
    setBusy(extra.kind === "backing" ? "Importing song…" : "Uploading voice to the vault…");
    try {
      const { file_id } = await api.uploadAudio(blob, filename);
      if (decoded) engine.cacheRaw(file_id, decoded);
      await addTracks([newTrack({ name: name || filename.replace(/\.[^.]+$/, "").slice(0, 32), file_id, ...extra })]);
      toast.success(extra.kind === "backing" ? "Song imported — hit Record to sing over it" : "Voice added");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy("");
  };

  const importFiles = async (files, kind) => {
    for (const f of files) {
      if (f.size > MAX_UPLOAD_MB * 1024 * 1024) {
        toast.error(`${f.name} is larger than ${MAX_UPLOAD_MB}MB`);
        continue;
      }
      let decoded;
      try {
        decoded = await engine.ensure().decodeAudioData(await f.arrayBuffer());
      } catch (e) {
        toast.error(`${f.name} can't be played in this browser — try MP3, WAV or M4A`);
        continue;
      }
      await addFile(f, f.name, null, kind === "backing" ? { kind: "backing", volume: 0.7, reverb: 0 } : {}, decoded);
    }
  };

  const addDemo = async () => {
    setBusy("Summoning the demo chant…");
    try {
      const buf = await synthDemoVocal();
      const { file_id } = await api.uploadAudio(encodeWav(buf), "demo-chant.wav");
      engine.cacheRaw(file_id, buf);
      await addTracks([newTrack({ name: "Demo Chant", file_id })]);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy("");
  };

  useEffect(() => {
    if (booted.current) return;
    booted.current = true;
    engine.reset();
    api
      .getProject(id)
      .then(async (p) => {
        engine.sync(p);
        setProject(p);
        setSelectedId(p.tracks[0]?.id || null);
        for (const t of p.tracks) await loadTrack(t, p);
        if (params.get("demo") && !p.tracks.length) addDemo();
      })
      .catch((e) => {
        toast.error(errMsg(e));
        nav("/");
      });
    return () => engine.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (project) engine.sync(project);
  }, [project]);

  useEffect(() => {
    if (!playing) return;
    let raf;
    const tick = () => {
      const t = engine.getTime();
      if (!engine.loop && t >= engine.duration) {
        engine.stop();
        setPlaying(false);
        setTime(0);
        return;
      }
      setTime(t);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing]);

  const activeLoop = (p = project) => (p?.loop?.enabled && p.loop.end > p.loop.start ? p.loop : null);
  const play = () => {
    engine.play(time >= engine.duration ? 0 : time, 0, activeLoop());
    setPlaying(true);
  };
  const restartIfPlaying = (p) => {
    if (engine.playing) engine.play(engine.getTime(), 0, activeLoop(p));
  };
  const setLoopRegion = (a, b) => {
    const beat = 60 / project.bpm;
    const start = Math.max(0, Math.round(a / beat) * beat);
    const end = Math.max(start + beat, Math.round(b / beat) * beat);
    const next = { ...project, loop: { start, end, enabled: true } };
    change((p) => ({ ...p, loop: next.loop }));
    restartIfPlaying(next);
  };
  const toggleLoop = () => {
    const next = { ...project, loop: { ...project.loop, enabled: !project.loop.enabled } };
    change((p) => ({ ...p, loop: next.loop }));
    restartIfPlaying(next);
  };
  const clearLoop = () => {
    const next = { ...project, loop: { start: 0, end: 0, enabled: false } };
    change((p) => ({ ...p, loop: next.loop }));
    restartIfPlaying(next);
  };
  const pause = () => {
    setTime(engine.pause());
    setPlaying(false);
  };
  const seek = (frac) => {
    const t = Math.max(0, Math.min(1, frac)) * engine.duration;
    setTime(t);
    if (engine.playing) engine.play(t, 0, activeLoop());
    else engine.offsetAt = t;
  };

  const harmonize = async (parts) => {
    const src = project.tracks.find((t) => t.id === selectedSource);
    if (!src) return;
    setBusy("Forging harmonies…");
    const tracks = parts.map((h, i) =>
      newTrack({
        name: `${h.name}`.slice(0, 36),
        kind: "harmony",
        file_id: src.file_id,
        ...(h.steps != null
          ? { mode: "diatonic", degrees: h.steps, semitones: 0 }
          : { semitones: Math.max(-24, Math.min(24, src.semitones + h.semitones)) }),
        trim: src.trim || 0,
        start: src.start || 0,
        volume: h.volume ?? 0.6,
        pan: h.pan ?? (i % 2 ? 0.45 : -0.45),
        reverb: h.reverb ?? 0.4,
        delay: h.delay ?? 0,
      })
    );
    await addTracks(tracks);
    setBusy("");
    toast.success(`${tracks.length} harmony voice${tracks.length > 1 ? "s" : ""} raised`);
  };

  const deleteTrack = (tid) => {
    haltPlayback();
    change((p) => ({ ...p, tracks: p.tracks.filter((t) => t.id !== tid) }));
    if (selectedId === tid) setSelectedId(project.tracks.find((t) => t.id !== tid)?.id || null);
  };

  const reloadTrack = (patch) => {
    const t = project.tracks.find((x) => x.id === selectedId);
    if (!t) return;
    haltPlayback();
    engine.processed.delete(t.id);
    setBufVersion((n) => n + 1);
    loadTrack({ ...t, ...patch });
  };

  const changeMeta = (patch) => {
    const next = { ...project, ...patch };
    change((p) => ({ ...p, ...patch }));
    if (!patch.key && !patch.scale) return;
    const inKey = project.tracks.filter((t) => t.mode === "diatonic");
    if (!inKey.length) return;
    haltPlayback();
    inKey.forEach((t) => engine.processed.delete(t.id));
    setBufVersion((n) => n + 1);
    inKey.forEach((t) => loadTrack(t, next));
  };

  const recMeta = useRef({});
  const armRecording = ({ backing, countIn, click }) => {
    const ctx = engine.ensure();
    ctx.resume();
    const loop = isPro ? activeLoop() : null;
    const from = loop ? loop.start : time;
    const lead = countIn ? (4 * 60) / project.bpm : 0;
    if (click) engine.startClick(project.bpm, ctx.currentTime + 0.05 + lead - from);
    else if (countIn) engine.countIn(project.bpm, 4);
    if (backing) {
      engine.play(from, lead, loop);
      setPlaying(true);
    }
    recMeta.current = { start: from, lead, aligned: backing || countIn || click, loop };
    return lead;
  };
  const disarmRecording = () => {
    engine.stop();
    setPlaying(false);
  };

  const saveLoopTakes = async (buf, loop, n, lat) => {
    const ctx = engine.ensure();
    const L = loop.end - loop.start;
    const body = trimStart(ctx, buf, lat);
    const passes = Math.max(1, Math.floor(body.duration / L + 0.02));
    setBusy(`Sealing ${passes} loop take${passes > 1 ? "s" : ""}…`);
    const tracks = [];
    for (let i = 0; i < passes; i++) {
      const seg = sliceBuffer(ctx, body, i * L, L);
      const { file_id } = await api.uploadAudio(encodeWav(seg), `loop-take-${i + 1}.wav`);
      engine.cacheRaw(file_id, seg);
      tracks.push(newTrack({ name: `Take ${n} · pass ${i + 1}`, file_id, start: loop.start, mute: i !== passes - 1 }));
    }
    await addTracks(tracks);
    setSelectedId(tracks[tracks.length - 1].id);
    setBusy("");
    toast.success(`${passes} take${passes > 1 ? "s" : ""} recorded — latest pass unmuted, others muted for comparison`);
  };

  const handleTake = async (blob, inLatency) => {
    setRecOpen(false);
    const { start = 0, lead = 0, aligned, loop } = recMeta.current;
    const n = project.tracks.filter((t) => t.kind !== "backing").length + 1;
    const name = `Take ${n}`;
    const trim = aligned ? Math.min(1, engine.latency + inLatency) : 0;
    if (loop) {
      try {
        const ctx = engine.ensure();
        const buf = await ctx.decodeAudioData(await blob.arrayBuffer());
        await saveLoopTakes(buf, loop, n, lead + trim);
      } catch (e) {
        setBusy("");
        toast.error(`Loop takes failed: ${errMsg(e)}`);
      }
      return;
    }
    let file = blob;
    let filename = `recording.${blob.type.includes("mp4") ? "m4a" : blob.type.includes("ogg") ? "ogg" : "webm"}`;
    if (lead > 0) {
      try {
        const ctx = engine.ensure();
        const cut = trimStart(ctx, await ctx.decodeAudioData(await blob.arrayBuffer()), lead);
        file = encodeWav(cut);
        filename = "recording.wav";
      } catch (e) {
        toast.error("Could not remove count-in from take");
      }
    }
    addFile(file, filename, name, { trim, start });
  };

  const isDemo = !!project && !project.owner_id;

  const saveCopy = async () => {
    if (!user) return nav("/login");
    setSaving(true);
    try {
      const p = await api.duplicateProject(id, { ...strip(projectRef.current), name: `${projectRef.current.name} (copy)` });
      toast.success("Copied into your crypt");
      window.location.assign(`/studio/${p.id}`);
    } catch (e) {
      if (e.response?.status === 402) openUpgrade(errMsg(e));
      else toast.error(errMsg(e));
    }
    setSaving(false);
  };

  const save = async () => {
    if (isDemo) return saveCopy();
    setSaving(true);
    try {
      const p = await api.updateProject(id, strip(projectRef.current));
      setProject((cur) => ({ ...cur, updated_at: p.updated_at }));
      setDirty(false);
      toast.success("Project sealed in the vault");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setSaving(false);
  };

  const exportWav = async (vocalsOnly) => {
    if (!isPro) return openUpgrade("WAV export (full mix and vocals only) is a Pro feature.");
    if (vocalsOnly && !project.tracks.some((t) => t.kind !== "backing" && engine.processed.has(t.id))) {
      toast.error("No vocal tracks to export");
      return;
    }
    setExporting(true);
    try {
      const blob = await engine.exportWav(project, vocalsOnly);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${project.name.replace(/[^\w-]+/g, "_") || "mix"}${vocalsOnly ? "_vocals" : ""}.wav`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
      toast.success(vocalsOnly ? "Vocals rendered to WAV" : "Mix rendered to WAV");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setExporting(false);
  };

  const [sourceId, setSourceId] = useState(null);
  const selectedSource = project?.tracks.some((t) => t.id === sourceId) ? sourceId : project?.tracks.find((t) => t.kind === "lead")?.id || project?.tracks.find((t) => t.kind !== "backing")?.id;

  if (!project)
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="studio-loading">
        <Loader2 className="animate-spin text-[#D99B43]" />
      </div>
    );

  const selected = project.tracks.find((t) => t.id === selectedId);
  const duration = engine.duration;

  return (
    <div className="min-h-screen flex flex-col bg-[#080708]" data-testid="studio-page">
      <StudioHeader name={project.name} onName={(name) => change((p) => ({ ...p, name }))} dirty={dirty} saving={saving} exporting={exporting} onSave={save} onExport={exportWav} canExport={duration > 0} isDemo={isDemo} homeTo={user ? "/library" : "/"} onShare={() => (isPro ? setShareOpen(true) : openUpgrade("Shareable listen links for your finished mixes are a Pro feature."))} />
      <Transport
        playing={playing}
        time={time}
        duration={duration}
        project={project}
        onPlay={play}
        onPause={pause}
        onStop={haltPlayback}
        onRecord={() => setRecOpen(true)}
        onFile={(files) => importFiles(files, "lead")}
        onSong={(files) => importFiles(files, "backing")}
        songRef={songRef}
        onLoopToggle={toggleLoop}
        onLoopClear={clearLoop}
        onMeta={changeMeta}
        uploadRef={uploadRef}
      />
      <main className="flex-1 grid grid-cols-12 min-h-[calc(100vh-9rem)]">
        <aside className="col-span-12 lg:col-span-3 border-r border-[#352d40] bg-[#0d0b0f] p-5 overflow-y-auto vox-scroll lg:max-h-[calc(100vh-9rem)]">
          <HarmonyForge project={project} sourceId={selectedSource} setSourceId={setSourceId} busy={!!busy} onHarmonize={harmonize} isPro={isPro} onUpgrade={() => openUpgrade("The AI Grimoire suggests harmony parts for your vocal. It is part of Pro.")} />
        </aside>
        <section className="col-span-12 lg:col-span-6 p-5 flex flex-col gap-3 overflow-y-auto vox-scroll lg:max-h-[calc(100vh-9rem)] relative">
          {busy && (
            <div data-testid="busy-indicator" className="flex items-center gap-2 eyebrow text-[#D99B43]">
              <Loader2 size={14} className="animate-spin" /> {busy}
            </div>
          )}
          <Lanes
            tracks={project.tracks}
            buffers={engine.processed}
            duration={duration || 10}
            time={time}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onUpdate={updateTrack}
            onDelete={deleteTrack}
            onSeek={seek}
            onDemo={addDemo}
            onRecord={() => setRecOpen(true)}
            onUpload={() => uploadRef.current?.click()}
            onSong={() => songRef.current?.click()}
            loop={project.loop}
            onLoop={setLoopRegion}
            onDragEnd={() => restartIfPlaying(projectRef.current)}
          />
        </section>
        <aside className="col-span-12 lg:col-span-3 border-l border-[#352d40] bg-[#120f17] p-5 overflow-y-auto vox-scroll lg:max-h-[calc(100vh-9rem)]">
          <ChannelStrip track={selected} master={project.master} onUpdate={(patch) => updateTrack(selectedId, patch)} onMaster={updateMaster} onReload={reloadTrack} />
        </aside>
      </main>
      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} project={project} artistDefault={user?.name} />
      <DropOverlay onSongs={(f) => importFiles(f, "backing")} onVocals={(f) => importFiles(f, "lead")} />
      <RecordDialog
        open={recOpen}
        onOpenChange={setRecOpen}
        hasTracks={duration > 0}
        startAt={time}
        loop={isPro ? activeLoop() : null}
        loopLocked={!isPro && !!activeLoop()}
        onUpgrade={() => openUpgrade("Loop punch-in recording, with a new take every pass, is a Pro feature.")}
        bpm={project.bpm}
        onArm={armRecording}
        onDisarm={disarmRecording}
        onDone={handleTake}
      />
    </div>
  );
}
